import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  Building2,
  MapPin,
  PlusCircle,
  Trash2,
  Edit2,
  Search as SearchIcon,
  Info,
  AlertTriangle,
  ChevronRight,
  Database
} from "lucide-react";
import { getAllSites, setSite, deleteSite } from "../../store/actions";
import {
  Button,
  Table,
  Breadcrumb,
  Modal,
  useToast,
  Card,
  Badge
} from "../UI";
import axios from "axios";
import { useTableSearch } from "../Common/useTableSearch";



const SiteList = ({
  sites,
  setSite,
  singleSite,
  deleteSite,
}: any) => {
  const { toast } = useToast();
  const history = useHistory();
  const [searchVal, setSearchVal] = useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState<any>(null);

  const [refreshToggle, setRefreshToggle] = useState(0);

  const fetchSites = React.useCallback(async () => {
    const { data } = await axios.get(`api/sites/getallsite`);
    return { data };
  }, [refreshToggle]);

  const { filteredData, loading } = useTableSearch({
    searchVal,
    retrieve: fetchSites,
  });

  const handleDelete = async () => {
    if (siteToDelete) {
      try {
        await deleteSite(siteToDelete.id || siteToDelete._id);
        setRefreshToggle(prev => prev + 1);
        toast("Site deleted successfully", "success");
        setIsDeleteModalOpen(false);
        setSiteToDelete(null);
      } catch (error) {
        toast("Failed to delete site", "error");
      }
    }
  };

  const columns = [
    {
      title: "Site Name",
      key: "sitename",
      width: "40%",
      render: (val: any) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#8dc63f]/15 flex items-center justify-center shrink-0">
            <Building2 className="w-6 h-6 text-[#8dc63f]" />
          </div>
          <span className="font-semibold text-xs md:text-sm text-slate-800 tracking-tight">{val}</span>
        </div>
      )
    },
    {
      title: "Region",
      key: "region",
      width: "35%",
      render: (val: any) => (
        <div className="flex items-center gap-2 text-slate-600 font-medium text-xs md:text-sm">
          <span>{val || "N/A"}</span>
        </div>
      )
    },
    {
      title: "Actions",
      key: "actions",
      width: "25%",
      render: (_: any, record: any) => (
        <div className="flex items-center gap-1.5">
          <Link to="/editsite" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0 text-slate-400 hover:text-[#8dc63f] hover:bg-[#8dc63f]/10 transition-colors flex items-center justify-center border-none bg-transparent rounded-lg"
              onClick={() => setSite(record)}
            >
              <Edit2 className="w-4 h-4 text-[#8dc63f]" />
            </Button>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="h-9 w-9 p-0 text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors flex items-center justify-center border-none bg-transparent rounded-lg"
            onClick={(e) => { e.stopPropagation(); setSiteToDelete(record); setIsDeleteModalOpen(true); }}
          >
            <Trash2 className="w-4 h-4 text-rose-600" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <Breadcrumb
          items={[
            { label: 'Home', to: '/dashboard' },
            { label: 'Sites' }
          ]}
        />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-[#8dc63f]/15 rounded-xl flex items-center justify-center">
              <Building2 className="w-4 h-4 text-[#8dc63f]" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight leading-none">Sites</h1>
            {filteredData.length > 0 && (
              <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                {filteredData.length}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative group">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-[#8dc63f] transition-colors" />
              <input
                type="text"
                placeholder="Search sites..."
                onChange={(e) => setSearchVal(e.target.value)}
                className="pl-9 pr-3 h-8 bg-slate-50 border border-slate-100 rounded-lg text-xs font-normal focus:bg-white focus:border-[#8dc63f] focus:ring-2 focus:ring-[#8dc63f]/5 transition-all outline-none w-52"
              />
            </div>

            <Link to="/addsite">
              <Button className="h-10 px-3 gap-1.5 text-xs font-semibold bg-[#8dc63f] hover:bg-[#76ab32] shadow-sm">
                <PlusCircle className="w-3.5 h-3.5" />
                Add Site
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <Table
        className="max-w-5xl"
        columns={columns}
        dataSource={filteredData}
        rowKey="_id"
        onRowClick={(record) => {
          setSite(record);
          // Navigate to the Door List page with site filter
          history.push(`/doorlist?siteId=${record.id || record._id}`);
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirm Deletion"
        footer={
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete} className="gap-2">
              <Trash2 className="w-4 h-4" />
              Delete Site
            </Button>
          </div>
        }
      >
        <div className="flex gap-4">
          <div className="p-3 bg-rose-100 rounded-2xl h-fit">
            <AlertTriangle className="w-6 h-6 text-rose-600" />
          </div>
          <div className="space-y-2">
            <p className="text-slate-900 font-bold">Are you sure you want to delete this site?</p>
            <p className="text-sm text-slate-500 font-medium leading-relaxed">
              Deleting <span className="text-slate-900 font-extrabold">{siteToDelete?.sitename}</span> will remove it from all associated request forms. This action cannot be undone.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  sites: state.site.siteData,
  singleSite: state.site.singleSite,
});

const mapDispatchToProps = {
  getAllSites,
  setSite,
  deleteSite,
};

export default connect(mapStateToProps, mapDispatchToProps)(SiteList);



